import { useEffect, useMemo, useState } from 'react';
import { collection, onSnapshot } from 'firebase/firestore';

import { db } from '../firebase/firebase';
import { useUserData } from './useUserData';

export interface ClanMember {
  uid: string;
  name: string;
  referralCode: string;
  parentUid: string;
  joinedAt?: Date;
}

export const useClanMembers = (
  clanRootUid?: string | null,
  currentUid?: string | null,
) => {
  const [members, setMembers] = useState<ClanMember[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!clanRootUid) {
      setMembers([]);
      setLoading(false);
      return;
    }

    const ref = collection(db, 'users', clanRootUid, 'children');

    const unsubscribe = onSnapshot(ref, (snapshot) => {
      const data: ClanMember[] = snapshot.docs
        .map((doc) => {
          const d = doc.data();

          return {
            uid: d.uid,
            name: d.name,
            referralCode: d.referralCode,
            parentUid: clanRootUid,
            joinedAt: d.joinedAt?.toDate?.() ?? undefined,
          };
        })
        .filter((member) => member.uid !== currentUid);

      setMembers(data);
      setLoading(false);
    });

    return () => unsubscribe();
  }, [clanRootUid, currentUid]);

  return {
    members,
    count: members.length,
    loading,
  };
};

export function useIsClanMember() {
  const { userData } = useUserData();

  const hasJoined = useMemo(() => {
    if (!userData) return false;

    return Boolean(userData.isClanRoot || userData.parentUid);
  }, [userData]);

  return { hasJoined };
}
